/**
 * Coordinate normalization for tactical board + video telestrator.
 *
 * The persisted JSON (`tactical_board_json`, `coaching_notes.drawing`,
 * `coaching_clips.drawing`) stores ALL coordinates as floats in [0, 1]
 * relative to the rendered surface (pitch viewbox for the tactical
 * board, video frame for the telestrator). The canvas renderers work
 * in pixels — Konva nodes carry pixel coords on `x` / `y` / `width` /
 * `height`. This module is the conversion layer.
 *
 * Why a separate module rather than inline conversion at every shape
 * render site:
 *   1. Unit-testable boundary. The legacy painter and tactical-board
 *      both `clamp01` on every coordinate read, which papered over a
 *      class of bugs (NaN points snap to 0; negative coords snap to
 *      0; coords > 1 snap to 1 — silent data loss). Centralizing the
 *      conversion lets us hold one place to the contract.
 *   2. Single conversion direction at a time. The canvas itself
 *      never deals with normalized coords: it consumes `px*` shapes
 *      from `toPx(scene, dims)` and emits `unit*` shapes via
 *      `toUnit(node, dims)` at save time.
 *
 * Bounds:
 *   - Inputs are clamped to [0, 1] via `clamp01`. Out-of-range or
 *     non-finite values become 0 silently (matches the backend's
 *     `_validate_board_unit` behavior in models.py — except the
 *     backend raises 422 on out-of-range while the client clamps;
 *     the validation happens at save time via the schema mirror in
 *     `./board-schema.ts`).
 *   - Width/height for zones MUST be >= 0.02 to satisfy the backend's
 *     minimum-size check. This is the canvas's responsibility (the
 *     resize handler), not this module's.
 */

/** Pitch render dimensions used by both the tactical board canvas and
 * the persisted SVG renderer in `js/tactical-board.js` (legacy
 * compatibility). The 1050x680 ratio comes from a real soccer pitch
 * aspect (~1.54:1) and matches the legacy code so screenshots stay
 * stable across the rewrite.
 *
 * The canvas resizes the Konva stage to fit its container, scaling
 * by `containerWidth / PITCH_W`. The persisted JSON uses normalized
 * 0..1 coords, so the render resolution is purely a display concern. */
export const PITCH_W = 1050;
export const PITCH_H = 680;

/** Stage dimensions when neither a pitch nor a video frame is the
 * authority (admin previews, screenshot capture, etc.). Tests may
 * inject their own via `Dims`. */
export type Dims = { width: number; height: number };

export const PITCH_DIMS: Dims = { width: PITCH_W, height: PITCH_H };

/** Clamp a numeric input to [0, 1]. Non-finite values (NaN, Infinity)
 * are coerced to 0 — same behavior as the legacy code, intentionally
 * silent so a corrupted client cannot crash the canvas. */
export function clamp01(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Normalize a pixel coordinate to [0, 1]. */
export function pxToUnit(px: number, axisExtent: number): number {
  if (axisExtent <= 0) return 0;
  return clamp01(px / axisExtent);
}

/** Convert a 0..1 unit coordinate to pixels for rendering. */
export function unitToPx(unit: number, axisExtent: number): number {
  return clamp01(unit) * axisExtent;
}

/** Point in either coordinate space. The discriminator is the caller —
 * passing a unit point to a px renderer is a bug Konva will silently
 * draw at the top-left. */
export type Point = { x: number; y: number };

/** Convert a `{x, y}` point from unit coords to pixel coords. */
export function pointToPx(p: Point, dims: Dims): Point {
  return {
    x: unitToPx(p.x, dims.width),
    y: unitToPx(p.y, dims.height),
  };
}

/** Convert a `{x, y}` point from pixel coords to unit coords. */
export function pointToUnit(p: Point, dims: Dims): Point {
  return {
    x: pxToUnit(p.x, dims.width),
    y: pxToUnit(p.y, dims.height),
  };
}

/** Convert a width / height pair (used by zones and spotlights). The
 * floor of zero keeps zones invertible during a drag — caller is
 * responsible for enforcing the 0.02 minimum at save time. */
export function sizeToPx(size: { w: number; h: number }, dims: Dims): { w: number; h: number } {
  return {
    w: unitToPx(size.w, dims.width),
    h: unitToPx(size.h, dims.height),
  };
}

export function sizeToUnit(size: { w: number; h: number }, dims: Dims): { w: number; h: number } {
  return {
    w: pxToUnit(size.w, dims.width),
    h: pxToUnit(size.h, dims.height),
  };
}

/** Decimate a freehand point sequence to drop near-duplicates (helps
 * keep the persisted blob small + matches the legacy painter's
 * behavior in `js/coaching/review.js`). Points whose Manhattan
 * distance from the previous emitted point is below `epsilon` (unit
 * coords) are dropped. The first and last points are always kept. */
export function decimateFreehand(points: Point[], epsilon = 0.005): Point[] {
  if (points.length <= 2) return [...points];
  const out: Point[] = [points[0]!];
  for (let i = 1; i < points.length - 1; i++) {
    const last = out[out.length - 1]!;
    const p = points[i]!;
    if (Math.abs(p.x - last.x) + Math.abs(p.y - last.y) >= epsilon) {
      out.push(p);
    }
  }
  out.push(points[points.length - 1]!);
  return out;
}
